import type {
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";
import { useMutation, useQuery } from "@tanstack/react-query";
import type { AxiosError } from "axios";

import ClientBase from "@citrine/client";

import { BranchRootClient } from "../branch-root";
import { DesignWorkflowClient } from "../design-workflow";
import { PredictorClient } from "../predictor";

import type {
  Branch,
  BranchAssetVersionUpdates,
  BranchVersionUpdate,
} from "./branch";

interface CreateBranchVersionProps {
  updates: BranchVersionUpdate;
  rootId: string;
  retrainModels?: boolean;
}

export class BranchClient extends ClientBase<Branch> {
  protected constructor(protected readonly projectId: string) {
    super({
      endpoint: `/api/v2/projects/${projectId}/branches`,
      findField: "response",
      defaultQuery: {
        archived: undefined,
        root: undefined,
        version: undefined,
        page: 1,
        // these objects are lightweight, so we can request more than 20 at a time
        per_page: 100,
      },
    });
  }

  static getInstance(projectId: string) {
    return new this(projectId);
  }

  async archive(branchId: string): Promise<Branch> {
    const result = await this.request<Branch>({
      method: "PUT",
      url: `${this.endpoint}/${branchId}/archive`,
    });
    this.invalidateAfterMutation(result.id);
    this.addModelToCache(result);
    return result;
  }

  useArchive(
    options: Omit<
      UseMutationOptions<Branch, AxiosError, string>,
      "mutationFn"
    > = {}
  ): UseMutationResult<Branch, AxiosError, string> {
    return useMutation<Branch, AxiosError, string>({
      ...options,
      mutationFn: (branchId) => this.archive(branchId),
    });
  }

  async create(branch: Branch) {
    const result = await super.create(branch);

    // invalidate branch root lists, and the branch root in question if it exists
    BranchRootClient.getInstance(this.projectId).invalidateAfterMutation(
      branch.metadata?.root_id
    );

    return result;
  }

  async restore(branchId: string): Promise<Branch> {
    const result = await this.request<Branch>({
      method: "PUT",
      url: `${this.endpoint}/${branchId}/restore`,
    });
    this.invalidateAfterMutation(result.id);
    this.addModelToCache(result);
    return result;
  }

  useRestore(
    options: Omit<
      UseMutationOptions<Branch, AxiosError, string>,
      "mutationFn"
    > = {}
  ) {
    return useMutation<Branch, AxiosError, string>({
      ...options,
      mutationFn: (branchId) => this.restore(branchId),
    });
  }

  async createBranchVersion({
    rootId,
    updates,
    retrainModels = false,
  }: CreateBranchVersionProps) {
    const result = await this.request({
      url: `${this.endpoint}/next-version-predictor`,
      method: "POST",
      data: updates,
      params: {
        root: rootId,
        retrain_models: retrainModels,
      },
    });

    // we need to invalidate workflow queries because we just created a new one
    DesignWorkflowClient.getInstance(this.projectId).invalidateAfterMutation();

    // The new branch has new versions of the same models that need
    // to be refreshed. May need to do this for design spaces in the future
    PredictorClient.getInstance(this.projectId).invalidateAfterMutation();

    BranchRootClient.getInstance(this.projectId).invalidateAfterMutation();

    // shouldn't need to invalidate this branch since it's new
    this.invalidateAfterMutation();
    this.addModelToCache(result);
    return result;
  }

  useCreateBranchVersion(
    options: Omit<
      UseMutationOptions<Branch, AxiosError, CreateBranchVersionProps>,
      "mutationFn"
    > = {}
  ) {
    return useMutation<Branch, AxiosError, CreateBranchVersionProps>({
      ...options,
      mutationFn: (args) => this.createBranchVersion(args),
    });
  }

  getAssetUpdates(branchId: string) {
    return this.request<BranchAssetVersionUpdates>({
      method: "GET",
      url: `${this.endpoint}/${branchId}/data-version-updates-predictor`,
    });
  }

  public useGetAssetUpdates(
    branchId: string,
    options?: Omit<
      UseQueryOptions<BranchAssetVersionUpdates>,
      "queryKey" | "queryFn"
    >
  ): UseQueryResult<BranchAssetVersionUpdates> {
    return useQuery({
      ...options,
      queryKey: ["branch-asset-updates-key", branchId],
      queryFn: () => this.getAssetUpdates(branchId),
    });
  }

  protected fromModel(model: Partial<Branch>) {
    return model.data;
  }
}
